import React, { useState } from 'react';
import { IonButton, IonSkeletonText } from '@ionic/react';
import './AddonCard.css';

/**
 * AddonCard — generic credits-billed addon panel.
 *
 * One card per addon (Dedicated Egress, Warm Pool, …). The card itself
 * is dumb — it renders title / description / rate / detail row / actions
 * and delegates everything stateful (enable, disable, quantity, copy)
 * to the parent via callbacks. That keeps test setup simple and lets the
 * BillingPage own the loading-state machine for the whole section.
 *
 * Visual conventions:
 *   - Disable buttons use `.btn-text-danger` (red text, no red fill) —
 *     a softer affordance than the legacy `.btn-danger` Cancel button
 *     so the user doesn't feel cornered into staying enabled.
 *   - Insufficient-balance is rendered inline (not as a popup) — we
 *     LET the user click enable; the backend returns 402 and the parent
 *     toasts. Pre-flight warning is informational only.
 */
const AddonCard = ({
  name,
  description,
  perDayUsd,                 // number — display "$X.YY/day"
  perMonthEstimateUsd,       // optional — display "(~ $Z/month)"
  enabled,                   // bool
  detailRows,                // array of [label, value] tuples
  insufficientBalance,       // bool — show inline warning
  busy,                      // bool — disables action buttons during a request
  onEnable,                  // () => void
  onDisable,                 // () => void
  onAddCredits,              // () => void  shown only when insufficientBalance
  children,                  // extra controls (e.g. quantity stepper)
  placeholder,               // bool — render a muted "coming soon" card (no actions, no rate)
}) => {
  // Placeholder variant: a muted, non-interactive card used to fill an
  // otherwise-empty grid slot so the 2-up Add-ons row reads as balanced.
  // No rate / detail rows / action button — just title + subtext, greyed.
  if (placeholder) {
    // Skeleton-only placeholder (no text) — hints "more coming" without
    // copy. Mirrors the real card's layout: a title bar + a pill, then two
    // description lines. NOT animated (no `animated` prop) — a static muted
    // placeholder, not a "loading" shimmer.
    return (
      <div className="addon-card addon-card--placeholder" aria-hidden="true">
        <div className="addon-card-header">
          <div className="addon-card-header-title">
            <IonSkeletonText style={{ width: '48%', height: '15px', borderRadius: '4px', margin: 0 }} />
          </div>
          <div className="addon-card-header-action">
            <IonSkeletonText style={{ width: '88px', height: '22px', borderRadius: '999px', margin: 0 }} />
          </div>
        </div>
        <IonSkeletonText style={{ width: '92%', height: '11px', borderRadius: '4px', marginTop: '14px' }} />
        <IonSkeletonText style={{ width: '68%', height: '11px', borderRadius: '4px', marginTop: '8px' }} />
      </div>
    );
  }

  const rate = typeof perDayUsd === 'number' && Number.isFinite(perDayUsd)
    ? `$${perDayUsd.toFixed(2)}/day`
    : '—';
  const monthly = typeof perMonthEstimateUsd === 'number' && Number.isFinite(perMonthEstimateUsd)
    ? `(~ $${perMonthEstimateUsd.toFixed(0)}/month)`
    : null;

  // Enable / Disable action moved to the header (top-right, aligned with
  // the title) so users don't have to scan to the bottom of the card. The
  // body below is then pure "what + how much" info, no action affordance
  // hidden under the rate / detail rows.
  const action = enabled ? (
    <button
      type="button"
      className="addon-card-disable"
      onClick={onDisable}
      disabled={busy}
    >
      {busy ? 'Disabling…' : 'Disable'}
    </button>
  ) : (
    // Reuse the shared .btn-white IonButton atom (used in project
    // settings, sentry page, etc.). .btn-white only styles IonButton
    // (uses --background CSS vars), not a plain <button>.
    <IonButton
      fill="clear"
      size="small"
      className="btn-white"
      onClick={insufficientBalance ? onAddCredits : onEnable}
      disabled={busy}
    >
      {busy ? 'Enabling…' : 'Enable'}
    </IonButton>
  );

  return (
    <div className={`addon-card ${enabled ? 'addon-card--enabled' : ''}`}>
      <div className="addon-card-header">
        <div className="addon-card-header-title">
          <h3 className="addon-card-name">{name}</h3>
          {enabled && <span className="addon-card-badge">Enabled</span>}
        </div>
        <div className="addon-card-header-action">{action}</div>
      </div>

      <p className="addon-card-desc">{description}</p>

      <div className="addon-card-rate">
        <span className="addon-card-rate-value">{rate}</span>
        {monthly && <span className="addon-card-rate-monthly">{monthly}</span>}
      </div>

      {enabled && Array.isArray(detailRows) && detailRows.length > 0 && (
        <div className="addon-card-details">
          {detailRows.map(([label, value]) => (
            <div className="addon-card-detail-row" key={label}>
              <span className="addon-card-detail-label">{label}</span>
              <span className="addon-card-detail-value">{value}</span>
            </div>
          ))}
        </div>
      )}

      {children && <div className="addon-card-children">{children}</div>}
    </div>
  );
};

/**
 * Quantity stepper — used inside an AddonCard's `children` slot when an
 * addon supports multi-instance scaling (e.g. Warm Pool). Tiny by design:
 * one number, two buttons, no input field. Hardened at min=1 / max=N.
 */
export const QuantityStepper = ({ value, min = 1, max = 10, onChange, busy }) => {
  const canDec = !busy && value > min;
  const canInc = !busy && value < max;
  return (
    <div className="addon-card-stepper" role="group" aria-label="Quantity">
      <button
        type="button"
        className="addon-card-stepper-btn"
        onClick={() => canDec && onChange(value - 1)}
        disabled={!canDec}
        aria-label="Decrease"
      >
        −
      </button>
      <span className="addon-card-stepper-value" aria-live="polite">{value}</span>
      <button
        type="button"
        className="addon-card-stepper-btn"
        onClick={() => canInc && onChange(value + 1)}
        disabled={!canInc}
        aria-label="Increase"
      >
        +
      </button>
    </div>
  );
};

/**
 * Toggle button that's distinct from Enable/Disable — used for inline
 * actions like "Copy IP". Kept here so addon-related affordances share
 * a stylesheet.
 */
export const CopyButton = ({ text, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // best-effort
    }
  };
  return (
    <button type="button" className="btn-link addon-card-copy" onClick={onClick}>
      {copied ? 'Copied' : label}
    </button>
  );
};

export default AddonCard;
